import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Declaration } from '../../types/declaration';
import { getDeclarationStatus, getRegYear, getStatusColor, getStatusCounts } from '../../utils/declarationUtils';

interface DeclarationsDynamicsProps {
    declarations: Declaration[];
    filteredYears?: string[];
    onBarClick?: (year: string) => void;
    height?: number;
}

// Динамика регистрации деклараций по годам в разрезе статусов
const DeclarationsDynamics: React.FC<DeclarationsDynamicsProps> = ({ declarations, filteredYears = [], onBarClick, height = 450 }) => {
    const statuses = getStatusCounts(declarations).map(item => item.status);

    const statusColors: { [key: string]: string } = {};
    statuses.forEach((status, index) => {
        statusColors[status] = getStatusColor(status, index);
    });

    const getNivoBarData = () => {
        const countsByYear: { [year: string]: { [status: string]: number } } = {};
        declarations.forEach(declaration => {
            const year = getRegYear(declaration);
            if (!year) return;
            const status = getDeclarationStatus(declaration);
            if (!countsByYear[year]) countsByYear[year] = {};
            countsByYear[year][status] = (countsByYear[year][status] || 0) + 1;
        });

        return Object.keys(countsByYear).sort().map(year => {
            const yearData: { [key: string]: string | number } = { year };
            statuses.forEach(status => {
                yearData[status] = countsByYear[year][status] || 0;
            });
            return yearData;
        });
    };

    const getIntegerTickValues = (maxValue: number): number[] => {
        if (!isFinite(maxValue) || maxValue <= 0) return [0];
        const niceStep = (value: number) => {
            const pow = Math.pow(10, Math.floor(Math.log10(value)));
            const mantissa = value / pow;
            let step: number;
            if (mantissa <= 1) step = 1;
            else if (mantissa <= 2) step = 2;
            else if (mantissa <= 5) step = 5;
            else step = 10;
            return step * pow;
        };
        const roughStep = Math.ceil(maxValue / 8);
        const step = Math.max(1, niceStep(roughStep));
        const upper = Math.ceil(maxValue / step) * step;
        const ticks: number[] = [];
        for (let tick = 0; tick <= upper; tick += step) ticks.push(tick);
        return ticks;
    };

    const getYearTotal = (yearData: { [key: string]: string | number }) => {
        return statuses.reduce((sum, status) => sum + Number(yearData[status] || 0), 0);
    };

    const nivoBarData = getNivoBarData();
    const maxYearTotal = nivoBarData.reduce((max, yearData) => Math.max(max, getYearTotal(yearData)), 0);

    if (nivoBarData.length === 0) {
        return <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет данных для отображения</div>;
    }

    return (
        <>
            <div style={{ display: 'flex', gap: 24, marginTop: 12, marginBottom: 8, justifyContent: 'center', flexWrap: 'wrap', padding: '0 20px' }}>
                {statuses.map(status => (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                        <span style={{
                            display: 'inline-block',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: statusColors[status],
                            marginRight: 6,
                            border: '1.5px solid #bbb',
                        }} />
                        <span style={{ fontWeight: 500 }}>{status}</span>
                    </div>
                ))}
            </div>
            <div style={{ width: '100%', height, position: 'relative' }}>
                <ResponsiveBar
                    data={nivoBarData}
                    keys={statuses}
                    indexBy="year"
                    margin={{ top: 50, right: 60, bottom: 80, left: 80 }}
                    padding={0.1}
                    groupMode="stacked"
                    colors={({ id, data }) => {
                        if (filteredYears.length > 0 && !filteredYears.includes(String(data.year))) {
                            return '#eee';
                        }
                        return statusColors[String(id)];
                    }}
                    borderRadius={4}
                    enableLabel={true}
                    labelSkipWidth={16}
                    labelSkipHeight={16}
                    labelTextColor="#ffffff"
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Год',
                        legendPosition: 'middle',
                        legendOffset: 40,
                    }}
                    axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Количество деклараций',
                        legendPosition: 'middle',
                        legendOffset: -50,
                        tickValues: getIntegerTickValues(maxYearTotal),
                    }}
                    tooltip={({ id, value, color, indexValue, data }) => {
                        const total = getYearTotal(data);
                        const percent = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : '0.0';
                        return (
                            <div style={{ padding: 12, background: '#fff', border: '1px solid #ccc', borderRadius: 6, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                    <strong style={{ color }}>{String(id)}</strong>
                                    <span style={{ fontSize: 13, color: '#888' }}>Год: <b>{String(indexValue)}</b></span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                    <span>Количество:</span>
                                    <b>{String(value)}</b>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                    <span>Процент:</span>
                                    <b>{percent}%</b>
                                </div>
                                <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
                                <div style={{ fontWeight: 500, fontSize: 13 }}>Всего за год: {total}</div>
                            </div>
                        );
                    }}
                    legends={[]}
                    theme={{
                        axis: {
                            legend: { text: { fontSize: 16, fontWeight: 600 } },
                            ticks: { text: { fontSize: 14 } },
                        },
                        tooltip: { container: { fontSize: 14 } },
                    }}
                    animate={true}
                    motionConfig="gentle"
                    onClick={bar => {
                        if (onBarClick) onBarClick(String(bar.data.year));
                    }}
                />
            </div>
        </>
    );
};

export default DeclarationsDynamics;
